import React, { useState, useEffect } from 'react';
import {
    Grid,
    Typography,
    TextField,
    Button,
    Dialog,
    DialogContent,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    FormHelperText,
} from '@mui/material';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { toast } from 'react-toastify';
import AppDialogTitle from '../../../ui/appDialogTitle';
import { isLocalDev } from '../../../../utils/environment';
import { LensConfigurationService, ValidDataService } from '../../../../service/validDataService';
import validLookup from '../../../../utils/validLookup';
import SafeDialogContent from 'components/ui/SafeDialogContent';

export const initialValuesLensModelForm = {
    manufacturerId: '',
    manufacturerBrandId: '',
    lensType: '',
    modelName: '',
    status: validLookup.validStatus.active,
    notes: '',
};

export default function ValidLensModelInputForm({ open, onClose, onRecordAdded }) {
    const [manufacturers, setManufacturers] = useState([]);
    const [brands, setBrands] = useState([]);
    const [addMfgOpen, setAddMfgOpen] = useState(false);
    const [addMfgSubmitting, setAddMfgSubmitting] = useState(false);
    const [newMfgName, setNewMfgName] = useState('');
    const [addBrandOpen, setAddBrandOpen] = useState(false);
    const [addBrandSubmitting, setAddBrandSubmitting] = useState(false);
    const [newBrandName, setNewBrandName] = useState('');

    useEffect(() => {
        if (open) {
            fetchManufacturers();
        }
    }, [open]);

    const fetchManufacturers = async () => {
        try {
            const result = await ValidDataService.getManufacturersByType('lens');
            const { code, data } = result;
            if (code === 0) {
                setManufacturers(data || []);
            }
        } catch (error) {
            if (isLocalDev) console.error(error);
            toast.error('Failed to load manufacturers');
        }
    };

    const loadBrandsForManufacturer = async (manufacturerId) => {
        try {
            const result = await ValidDataService.getManufacturerBrands(manufacturerId, 'lenses');
            const { code, data } = result;
            if (code === 0) {
                setBrands(data || []);
                return data || [];
            }
        } catch (error) {
            if (isLocalDev) console.error(error);
        }
        return [];
    };

    const handleManufacturerChange = async (manufacturerId, setFieldValue) => {
        setFieldValue('manufacturerId', manufacturerId);
        setFieldValue('manufacturerBrandId', '');
        setBrands([]);

        if (manufacturerId) {
            await loadBrandsForManufacturer(manufacturerId);
        }
    };

    const handleSubmit = async (values, { resetForm, setSubmitting }) => {
        try {
            const response = await LensConfigurationService.addModel(values)
            const { code, data, message } = response

            setSubmitting(false)

            if (code === 0) {
                toast.success(message);
                resetForm();
                onRecordAdded(data)
                onClose();
            } else {
                toast.error(message);
            }
        } catch (error) {
            if (isLocalDev) {
                console.error(error);
            }
            setSubmitting(false)
            toast.error('Failed to add lens model');
        }
    };

    return (
        <Dialog open={open} maxWidth={'sm'} fullWidth>
            <AppDialogTitle title={"Add Lens Model"} onClose={onClose} />
            <SafeDialogContent sx={{ mt: 1, mb: 1 }}>
                <Formik
                    initialValues={initialValuesLensModelForm}
                    onSubmit={handleSubmit}
                >
                    {({ isSubmitting, values, setFieldValue, handleChange, handleBlur, touched, errors }) => (
                        <Form>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <FormControl fullWidth size="small" error={touched.manufacturerId && !!errors.manufacturerId}>
                                        <InputLabel required>Manufacturer</InputLabel>
                                        <Select
                                            name="manufacturerId"
                                            value={values.manufacturerId}
                                            onChange={(e) => handleManufacturerChange(e.target.value, setFieldValue)}
                                            onBlur={handleBlur}
                                            label="Manufacturer"
                                        >
                                            <MenuItem value="">
                                                <em>None</em>
                                            </MenuItem>
                                            {manufacturers.map((mfr) => (
                                                <MenuItem key={mfr.id} value={mfr.id}>
                                                    {mfr.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <Button
                                            size="small"
                                            sx={{ mt: 0.5, textTransform: 'none' }}
                                            onClick={() => setAddMfgOpen(true)}
                                        >
                                            Don’t see it? Add manufacturer
                                        </Button>
                                        {touched.manufacturerId && errors.manufacturerId && (
                                            <FormHelperText>{errors.manufacturerId}</FormHelperText>
                                        )}
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl fullWidth size="small" disabled={!values.manufacturerId}>
                                        <InputLabel>Brand (Optional)</InputLabel>
                                        <Select
                                            name="manufacturerBrandId"
                                            value={values.manufacturerBrandId}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            label="Brand (Optional)"
                                        >
                                            <MenuItem value="">None</MenuItem>
                                            {brands.map((brand) => (
                                                <MenuItem key={brand.id} value={brand.id}>
                                                    {brand.brandName}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {!brands.length && values.manufacturerId && (
                                            <FormHelperText>No brands yet. Add one to keep things organized.</FormHelperText>
                                        )}
                                    </FormControl>
                                    <Button
                                        size="small"
                                        sx={{ mt: 0.5, textTransform: 'none' }}
                                        onClick={() => setAddBrandOpen(true)}
                                        disabled={!values.manufacturerId}
                                    >
                                        Add brand for this manufacturer
                                    </Button>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Lens Type</InputLabel>
                                        <Select
                                            name="lensType"
                                            value={values.lensType}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            label="Lens Type"
                                        >
                                            <MenuItem value="">None</MenuItem>
                                            <MenuItem value="single_vision">Single Vision</MenuItem>
                                            <MenuItem value="bifocal">Bifocal</MenuItem>
                                            <MenuItem value="trifocal">Trifocal</MenuItem>
                                            <MenuItem value="progressive">Progressive</MenuItem>
                                            <MenuItem value="aphakic">Aphakic</MenuItem>
                                            <MenuItem value="vocational">Vocational</MenuItem>
                                        </Select>
                                        <FormHelperText>The design type of this lens model</FormHelperText>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <Field
                                        as={TextField}
                                        name="modelName"
                                        label="Model Name"
                                        required
                                        fullWidth
                                        size="small"
                                        helperText="e.g., Varilux X, Eyezen+, Definity"
                                    />
                                    <ErrorMessage name="modelName">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12}>
                                    <Field
                                        as={TextField}
                                        name='status'
                                        label="Status"
                                        fullWidth
                                        select
                                        size="small"
                                    >
                                        <MenuItem value={"active"}>Active</MenuItem>
                                        <MenuItem value={"inactive"}>Inactive</MenuItem>
                                    </Field>
                                </Grid>
                                <Grid item xs={12}>
                                    <Field
                                        as={TextField}
                                        name="notes"
                                        label="Notes"
                                        multiline
                                        rows={2}
                                        fullWidth
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        disabled={isSubmitting}
                                    >
                                        Create
                                    </Button>
                                </Grid>
                            </Grid>

                            <Dialog open={addMfgOpen} onClose={() => setAddMfgOpen(false)} maxWidth="xs" fullWidth>
                                <AppDialogTitle title="Add Manufacturer" onClose={() => setAddMfgOpen(false)} />
                                <DialogContent>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        Create a manufacturer without leaving this form.
                                    </Typography>
                                    <TextField
                                        autoFocus
                                        fullWidth
                                        size="small"
                                        label="Manufacturer Name"
                                        value={newMfgName}
                                        onChange={(e) => setNewMfgName(e.target.value)}
                                        disabled={addMfgSubmitting}
                                    />
                                    <Button
                                        sx={{ mt: 2 }}
                                        variant="contained"
                                        onClick={async () => {
                                            if (!newMfgName.trim()) {
                                                toast.error('Please enter a manufacturer name');
                                                return;
                                            }

                                            setAddMfgSubmitting(true);
                                            try {
                                                const response = await ValidDataService.addValidManufacturer({
                                                    name: newMfgName.trim(),
                                                    type: 'lens',
                                                    status: validLookup.validStatus.active,
                                                });

                                                if (response.code === 0) {
                                                    toast.success('Manufacturer added');
                                                    await fetchManufacturers();
                                                    const newId = response.data?.id || '';
                                                    if (newId) {
                                                        await handleManufacturerChange(newId, setFieldValue);
                                                        setAddBrandOpen(true);
                                                    }
                                                    setNewMfgName('');
                                                    setAddMfgOpen(false);
                                                } else {
                                                    toast.error(response.message || 'Failed to add manufacturer');
                                                }
                                            } catch (error) {
                                                if (isLocalDev) {
                                                    console.error(error);
                                                }
                                                toast.error('Failed to add manufacturer');
                                            }
                                            setAddMfgSubmitting(false);
                                        }}
                                        disabled={addMfgSubmitting}
                                    >
                                        {addMfgSubmitting ? 'Saving...' : 'Save Manufacturer'}
                                    </Button>
                                </DialogContent>
                            </Dialog>

                            <Dialog open={addBrandOpen} onClose={() => setAddBrandOpen(false)} maxWidth="xs" fullWidth>
                                <AppDialogTitle title="Add Brand" onClose={() => setAddBrandOpen(false)} />
                                <DialogContent>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        Add a brand for the selected manufacturer.
                                    </Typography>
                                    <TextField
                                        autoFocus
                                        fullWidth
                                        size="small"
                                        label="Brand Name"
                                        value={newBrandName}
                                        onChange={(e) => setNewBrandName(e.target.value)}
                                        disabled={addBrandSubmitting}
                                    />
                                    <Button
                                        sx={{ mt: 2 }}
                                        variant="contained"
                                        onClick={async () => {
                                            if (!values.manufacturerId) {
                                                toast.error('Select a manufacturer first');
                                                return;
                                            }
                                            if (!newBrandName.trim()) {
                                                toast.error('Please enter a brand name');
                                                return;
                                            }

                                            setAddBrandSubmitting(true);
                                            try {
                                                const response = await ValidDataService.addBrandToManufacturer(values.manufacturerId, {
                                                    brandName: newBrandName.trim(),
                                                    type: 'lenses',
                                                    status: validLookup.validStatus.active,
                                                });

                                                const code = response?.code;
                                                const brandData = response?.data ?? response;

                                                if (code === 0 || brandData?.id) {
                                                    toast.success('Brand added');
                                                    const latest = await loadBrandsForManufacturer(values.manufacturerId);
                                                    const newId = brandData?.id;
                                                    if (newId) {
                                                        setFieldValue('manufacturerBrandId', newId);
                                                    } else if (latest.length === 1) {
                                                        setFieldValue('manufacturerBrandId', latest[0].id);
                                                    }
                                                    setNewBrandName('');
                                                    setAddBrandOpen(false);
                                                } else {
                                                    toast.error(response?.message || 'Failed to add brand');
                                                }
                                            } catch (error) {
                                                if (isLocalDev) {
                                                    console.error(error);
                                                }
                                                toast.error('Failed to add brand');
                                            }
                                            setAddBrandSubmitting(false);
                                        }}
                                        disabled={addBrandSubmitting}
                                    >
                                        {addBrandSubmitting ? 'Saving...' : 'Save Brand'}
                                    </Button>
                                </DialogContent>
                            </Dialog>
                        </Form>
                    )}
                </Formik>
            </SafeDialogContent>
        </Dialog>
    );
};